import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  FileText,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Phone,
  Building,
  Loader2,
  AlertTriangle,
  Shield,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const TaxInfoPage = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tanInfo, setTanInfo] = useState(null);
  const [communityInfo, setCommunityInfo] = useState(null);
  const [sampradayaOptions, setSampradayaOptions] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Form state
  const [hasTan, setHasTan] = useState(false);
  const [tanNumber, setTanNumber] = useState('');
  const [tanHolderName, setTanHolderName] = useState('');
  const [tanDocUrl, setTanDocUrl] = useState('');
  const [tanDocFileName, setTanDocFileName] = useState('');

  const [selectedSampradaya, setSelectedSampradaya] = useState('');
  const [sampradayaOther, setSampradayaOther] = useState('');
  const [templeName, setTempleName] = useState('');
  const [templeLocation, setTempleLocation] = useState('');
  const [associationYears, setAssociationYears] = useState('');
  const [referenceName, setReferenceName] = useState('');
  const [referencePhone, setReferencePhone] = useState('');
  const [referenceRelation, setReferenceRelation] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tanData, communityData, options] = await Promise.all([
        apiCall('/api/vendor-tax-info/tan'),
        apiCall('/api/vendor-tax-info/community'),
        apiCall('/api/vendor-tax-info/sampradaya-options'),
      ]);

      setTanInfo(tanData);
      setCommunityInfo(communityData);
      setSampradayaOptions(options.options || []);

      // Populate TAN form
      if (tanData.has_tan !== undefined) {
        setHasTan(tanData.has_tan);
        setTanNumber(tanData.tan_number || '');
        setTanHolderName(tanData.tan_holder_name || '');
        setTanDocUrl(tanData.tan_document_url || '');
        if (tanData.tan_document_url) {
          setTanDocFileName('TAN Certificate (uploaded)');
        }
      }

      // Populate community form
      if (communityData.sampradaya) {
        setSelectedSampradaya(communityData.sampradaya);
        setSampradayaOther(communityData.sampradaya_other || '');
        setTempleName(communityData.temple_name || '');
        setTempleLocation(communityData.temple_location || '');
        setAssociationYears(communityData.association_years?.toString() || '');
        setReferenceName(communityData.reference_name || '');
        setReferencePhone(communityData.reference_phone || '');
        setReferenceRelation(communityData.reference_relation || '');
      }
    } catch (error) {
      console.error('Failed to fetch tax info:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Handle TAN document upload
  const handleTanDocUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload a PDF or image file (JPG, PNG)',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Maximum file size is 10MB',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      const formData = new FormData();
      formData.append('files', file);

      const response = await fetch(`${BACKEND_URL}/api/files/upload-multiple?category=documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      const uploadedFile = result.files?.[0];
      
      if (uploadedFile?.success) {
        const fileUrl = uploadedFile.url.startsWith('http') 
          ? uploadedFile.url 
          : `${BACKEND_URL}${uploadedFile.url}`;
        setTanDocUrl(fileUrl);
        setTanDocFileName(file.name);
        toast({
          title: 'Upload Successful',
          description: 'TAN document uploaded successfully',
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload document',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTanSubmit = async () => {
    if (hasTan && !tanNumber) {
      toast({
        title: 'Error',
        description: 'Please enter your TAN number',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      await apiCall('/api/vendor-tax-info/tan', {
        method: 'POST',
        body: JSON.stringify({
          has_tan: hasTan,
          tan_number: hasTan ? tanNumber.toUpperCase() : null,
          tan_holder_name: hasTan ? tanHolderName : null,
          tan_document_url: hasTan ? tanDocUrl : null,
        }),
      });
      toast({
        title: 'Success',
        description: 'TAN information saved successfully',
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save TAN information',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCommunitySubmit = async () => {
    if (!selectedSampradaya || !referenceName || !referencePhone) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      await apiCall('/api/vendor-tax-info/community', {
        method: 'POST',
        body: JSON.stringify({
          sampradaya: selectedSampradaya,
          sampradaya_other: selectedSampradaya === 'Other' ? sampradayaOther : null,
          temple_name: templeName || null,
          temple_location: templeLocation || null,
          association_years: associationYears ? parseInt(associationYears) : null,
          reference_name: referenceName,
          reference_phone: referencePhone,
          reference_relation: referenceRelation || null,
        }),
      });
      toast({
        title: 'Success',
        description: 'Community information saved successfully',
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save community information',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const getVerificationBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <Badge className="bg-green-500">
            <CheckCircle className="h-3 w-3 mr-1" />
            Verified
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="warning" className="bg-yellow-500">
            <Clock className="h-3 w-3 mr-1" />
            Pending Verification
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return <Badge variant="outline">Not Submitted</Badge>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Tax &amp; Community Information
          </h1>
          <p className="text-muted-foreground">
            Complete your TAN details and community association for compliance
          </p>
        </div>

        {/* TAN Section */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  TAN Details
                </CardTitle>
                <CardDescription>
                  Tax Deduction Account Number for TDS compliance
                </CardDescription>
              </div>
              {tanInfo?.verification_status && getVerificationBadge(tanInfo.verification_status)}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Has TAN Checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hasTan"
                checked={hasTan}
                onCheckedChange={setHasTan}
              />
              <Label htmlFor="hasTan" className="font-medium">
                I have a TAN (Tax Deduction Account Number)
              </Label>
            </div>

            {hasTan && (
              <div className="space-y-4 pl-6 border-l-2 border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tanNumber">TAN Number *</Label>
                    <Input
                      id="tanNumber"
                      placeholder="e.g., DELH12345A"
                      value={tanNumber}
                      onChange={(e) => setTanNumber(e.target.value.toUpperCase())}
                      maxLength={10}
                    />
                    <p className="text-xs text-muted-foreground">
                      Format: 4 letters + 5 digits + 1 letter
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tanHolder">TAN Holder Name</Label>
                    <Input
                      id="tanHolder"
                      placeholder="Name as per TAN certificate"
                      value={tanHolderName}
                      onChange={(e) => setTanHolderName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>TAN Certificate Document</Label>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      {tanDocUrl ? (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-700 flex-1 truncate">
                            {tanDocFileName || 'Document uploaded'}
                          </span>
                          <a 
                            href={tanDocUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View
                          </a>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            onClick={() => {
                              setTanDocUrl('');
                              setTanDocFileName('');
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ) : (
                        <div className="relative">
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleTanDocUpload}
                            className="hidden"
                            id="tan-doc-upload"
                            disabled={uploading}
                          />
                          <label
                            htmlFor="tan-doc-upload"
                            className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {uploading ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span className="text-sm">Uploading...</span>
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4" />
                                <span className="text-sm">Click to upload TAN certificate</span>
                              </>
                            )}
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Upload your TAN certificate (PDF, JPG, or PNG, max 10MB) for verification
                  </p>
                </div>

                {tanInfo?.verification_status === 'rejected' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Rejection Reason: {tanInfo.rejection_reason}
                    </p>
                  </div>
                )}
              </div>
            )}

            <Button onClick={handleTanSubmit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save TAN Information'
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Community Section */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Community Association
                </CardTitle>
                <CardDescription>
                  Vaishnava community/temple association and reference
                </CardDescription>
              </div>
              {communityInfo?.reference_verified !== undefined && (
                communityInfo.reference_verified ? (
                  <Badge className="bg-green-500">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                ) : communityInfo.sampradaya ? (
                  <Badge variant="warning" className="bg-yellow-500">
                    <Clock className="h-3 w-3 mr-1" />
                    Pending
                  </Badge>
                ) : null
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Sampradaya Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vaishnava Sampradaya / Community *</Label>
                <Select value={selectedSampradaya} onValueChange={setSelectedSampradaya}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your sampradaya" />
                  </SelectTrigger>
                  <SelectContent>
                    {sampradayaOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedSampradaya === 'Other' && (
                <div className="space-y-2">
                  <Label>Specify Sampradaya *</Label>
                  <Input
                    placeholder="Enter your sampradaya name"
                    value={sampradayaOther}
                    onChange={(e) => setSampradayaOther(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Temple Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="templeName">
                  <Building className="h-4 w-4 inline mr-1" />
                  Temple / Center Name
                </Label>
                <Input
                  id="templeName"
                  placeholder="e.g., ISKCON Delhi"
                  value={templeName}
                  onChange={(e) => setTempleName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="templeLocation">Temple Location</Label>
                <Input
                  id="templeLocation"
                  placeholder="City, State"
                  value={templeLocation}
                  onChange={(e) => setTempleLocation(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="years">Years of Association</Label>
              <Input
                id="years"
                type="number"
                placeholder="e.g., 5"
                value={associationYears}
                onChange={(e) => setAssociationYears(e.target.value)}
                className="w-32"
                min={0}
              />
            </div>

            {/* Reference Contact */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-4 flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Reference Contact
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                Please provide a reference from your community who can vouch for your association.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="refName">Reference Name *</Label>
                  <Input
                    id="refName"
                    placeholder="Full name"
                    value={referenceName}
                    onChange={(e) => setReferenceName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refPhone">Phone Number *</Label>
                  <Input
                    id="refPhone"
                    placeholder="10-digit mobile"
                    value={referencePhone}
                    onChange={(e) => setReferencePhone(e.target.value)}
                    maxLength={10}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refRelation">Relation / Designation</Label>
                  <Input
                    id="refRelation"
                    placeholder="e.g., Temple President"
                    value={referenceRelation}
                    onChange={(e) => setReferenceRelation(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <Button onClick={handleCommunitySubmit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Community Information'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TaxInfoPage;
